import { NUMBERED_DOCUMENT_TYPES, isNumberedDocumentType, renderDocumentNumber, type NumberSeriesFormat, type NumberedDocumentType } from './numberSeries';
import { kindInPlainWords } from '../failures/notFound';

/**
 * Void, Delete and the manual number override (piece 22; decisions 0002,
 * 0197): the pure half of the lifecycle service every document stage calls.
 * Delete is absent once a document is numbered, sent or posted, and Void
 * stands in its place; a manual number is recorded, uniqueness-checked and
 * never moves the counter backwards.
 */
/** The nine types decision 0197 named, and the Work Order, numbered at creation with Void in its own transition table (decision 0387). */
export const VOIDABLE_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'WorkOrder', 'Bill', 'CreditNote', 'VendorCredit', 'Payment', 'Refund'] as const;
export type VoidableDocumentType = (typeof VOIDABLE_DOCUMENT_TYPES)[number];

export function isVoidable(documentType: string): documentType is VoidableDocumentType {
  return (VOIDABLE_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/**
 * 🔒 The types the lifecycle service holds an adapter for: the numbered set and
 * the voidable set together (piece 22). A Payment and a Refund are voidable and
 * take no number from the series (decision 0197), so a set keyed on the
 * numbered types alone had nowhere to hold the two documents Void reaches
 * without a number.
 */
export const LIFECYCLE_DOCUMENT_TYPES: readonly (NumberedDocumentType | VoidableDocumentType)[] = [...new Set([...NUMBERED_DOCUMENT_TYPES, ...VOIDABLE_DOCUMENT_TYPES])];

/** The type the lifecycle service's registry is keyed on, read off the one list above so the two cannot drift. */
export type LifecycleDocumentType = (typeof LIFECYCLE_DOCUMENT_TYPES)[number];

/** 🔨 A manual number override is refused by name for a type that takes no number, the Payment and the Refund among them. */
export function overrideTypeProblem(documentType: string): string | null {
  if (isNumberedDocumentType(documentType)) return null;
  return `The ${kindInPlainWords(documentType)} takes no document number, so it takes no manual override.`;
}

/** The AuditEvent action a Void writes, carrying the reason; the record and its number stay. */
export const DOCUMENT_VOIDED_ACTION = 'document.voided';

/** Void takes a reason; a Void with none is refused before any status moves. */
export function voidProblem(documentType: string, reason: string | null | undefined): string | null {
  if (!isVoidable(documentType)) return `${documentType} is not voided; it is canceled or closed through its own lifecycle.`;
  if (!reason || reason.trim().length === 0) return 'Void requires a reason.';
  return null;
}

export interface DeleteFacts {
  /** The document's own number; null on a Draft Invoice, which numbers at first send. */
  number: string | null;
  status: string;
  /** True once a posting or a send has happened, whatever the status column says. */
  isPosted?: boolean;
  isSent?: boolean;
}

/** A Draft that was never numbered is deleted, and that is the only delete (decision 0197). */
export function deleteAllowed(facts: DeleteFacts): boolean {
  return facts.number === null && facts.status === 'Draft' && !facts.isPosted && !facts.isSent;
}

export function deleteRefusal(documentType: string, facts: DeleteFacts): string {
  if (facts.number !== null) return `${documentType} ${facts.number} is numbered; it offers Void and no Delete.`;
  if (facts.isPosted) return `A posted ${documentType} offers Void and no Delete.`;
  if (facts.isSent) return `A sent ${documentType} offers Void and no Delete.`;
  return `A ${documentType} at ${facts.status} offers Void and no Delete.`;
}

/** An override names the number and says why; both are checked before the row is written. */
export function overrideProblem(issuedNumber: string | null | undefined, reason: string | null | undefined): string | null {
  if (!issuedNumber || issuedNumber.trim().length === 0) return 'A number override names the number issued; type the number this document is to carry.';
  if (!reason || reason.trim().length === 0) return 'A number override says why.';
  return null;
}

/**
 * The counter a manual number would occupy in the series, when it follows the
 * series' own format for the period the override is made in; null when it does
 * not, in which case the counter is left alone and only uniqueness holds.
 */
export function overrideCounter(issuedNumber: string, format: NumberSeriesFormat, at: Date, zone: string): number | null {
  const probe = renderDocumentNumber(format, 1, at, zone);
  const digits = String(1).padStart(Math.max(0, format.padding), '0');
  const stem = probe.slice(0, probe.length - digits.length);
  if (!issuedNumber.startsWith(stem)) return null;
  const tail = issuedNumber.slice(stem.length);
  if (!/^\d+$/.test(tail)) return null;
  const counter = Number.parseInt(tail, 10);
  return Number.isSafeInteger(counter) && counter >= 1 ? counter : null;
}

/** The counter after an override: forward when the manual number sits at or past it, otherwise unchanged; never backwards. */
export function nextNumberAfterOverride(currentNext: number, counter: number | null): number {
  if (counter === null) return currentNext;
  return Math.max(currentNext, counter + 1);
}
